import { Component, Input, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { AddToProductComponent } from 'src/app/barcode/add-to-product/add-to-product.component';
import { CartService } from 'src/app/cart/cart.service';
import { UploadComponent } from 'src/app/provider-price/upload/upload.component';
import { SupportService } from 'src/app/support.service';
import { environment } from 'src/environments/environment';
import { Product, ProductInCart, User } from 'src/typings';
import { AddImageComponent } from '../add-image/add-image.component';
import { AnalogueListComponent } from 'src/app/product/analogue-list/analogue-list.component';
import { EditStockComponent } from '../edit-stock/edit-stock.component';
import { FoldersComponent } from '../folders/folders.component';
import { PropertiesComponent } from '../info/properties/properties.component';
import { NameUpdateComponent } from '../name-update/name-update.component';
import { PrintPriceTagComponent } from '../print-price-tag/print-price-tag.component';
import { ProductService } from '../product.service';
import { RemoveDoubleComponent } from '../remove-double/remove-double.component';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from 'src/app/services/notification.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { SetNewPriceComponent } from '../set-new-price/set-new-price.component';

@Component({
  selector: 'app-product-list-item',
  templateUrl: './product-list-item.component.html',
  styleUrls: ['./product-list-item.component.scss']
})
export class ProductListItemComponent {
  @Input() product: Product | undefined;

  @ViewChild(MatMenuTrigger, { static: true }) matMenuTrigger: MatMenuTrigger | undefined;

  menuTopLeftPosition = { x: '0', y: '0' };

  @Input() set user(payload: User | undefined) {
    this._user = payload;
  }

  _user: User | undefined;

  @Input() even: boolean = false;

  get user() {
    return this._user;
  }

  @Input() cartProductsFrom: FormArray = this.fb.array([]);

  @Input() cartProducts: ProductInCart[] = [];

  constructor(
    public supportService: SupportService,
    private cartService: CartService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private productService: ProductService,
    private notificationService: NotificationService
  ) {}

  get img() {
    if (!this.product?.images || this.product.images.length === 0) return null;
    const img = this.product.images[0].name;
    return `${environment.BACKEND}/image/${img}`;
  }

  async addToCart() {
    if (!this.user || !this.user.cart || !this.product) return;
    await firstValueFrom(
      this.cartService.addProduct({
        id: this.user.cart.id,
        product_id: this.product.id
      })
    );
  }

  async removeFromCart() {
    if (!this.user || !this.user.cart || !this.product) return;
    await firstValueFrom(
      this.cartService.removeProduct({
        id: this.user.cart.id,
        product_id: this.product.id
      })
    );
  }

  productInCart() {
    if (!this.product) return;
    const index = this.cartProducts.findIndex((prod) => prod.product.id === this.product?.id);
    return index > -1;
  }

  getProductCartControl() {
    const prodCtrl = this.cartProductsFrom.controls.find((ctrl) => {
      return ctrl.value.product.id === this.product?.id;
    }) as FormGroup;
    if (!prodCtrl) return new FormControl();
    return prodCtrl.controls.quan as FormControl;
  }

  changeCartQuan() {
    if (!this.product) return;
    const group = this.getProductInCartGroup();
    const control = this.getProductCartControl();
    if (!control) return;
    this.cartService
      .updateProductInCart({
        id: group.value.id,
        quan: control.value,
        price: group.value.price
      })
      .subscribe(() => {
        // @todo improve\delete?
        return;
      });
  }

  getProductInCartGroup() {
    return this.cartProductsFrom.controls.find((ctrl) => {
      return ctrl.value.product.id === this.product?.id;
    }) as FormGroup;
  }

  openAnalogies(product: Product) {
    const dialog = this.dialog.open(AnalogueListComponent, {
      height: '700px',
      width: '1200px',
      panelClass: 'custom-dialog-container'
    });
    dialog.componentInstance.product = product;
  }

  removeDouble(product: Product) {
    const dialog = this.dialog.open(RemoveDoubleComponent);
    dialog.componentInstance.product = product;
    dialog.componentInstance.doneEvent.subscribe((data) => {
      if (data === true) dialog.close();
    });
  }

  addImage(product: Product) {
    const dialog = this.dialog.open(AddImageComponent);
    dialog.componentInstance.product = product;
    dialog.componentInstance.doneEvent.subscribe((data) => {
      if (data) dialog.close();
    });
  }

  updateName(product: Product) {
    const dialog = this.dialog.open(NameUpdateComponent);
    dialog.componentInstance.product = product;
    dialog.componentInstance.doneEvent.subscribe((data) => {
      if (data) dialog.close();
    });
  }

  printPriceTag(product: Product) {
    const dialog = this.dialog.open(PrintPriceTagComponent);
    dialog.componentInstance.product = product;
  }

  editProductInWarehouse(product: Product) {
    const row = product.stock.find((_row) => {
      return _row.warehouse.id == this.user?.defaultWarehouse.id;
    });
    if (!row) return this.notificationService.warning('Не нашел продукт');
    const dialogRef = this.dialog.open(EditStockComponent);
    dialogRef.componentInstance.row = row;
    dialogRef.componentInstance.doneEvent.subscribe((value) => {
      if (value) {
        return dialogRef.close();
      }
    });
  }

  addBarcode(product: Product) {
    const dialogRef = this.dialog.open(AddToProductComponent);
    dialogRef.componentInstance.product = product;
    dialogRef.componentInstance.doneEvent.subscribe(() => {
      dialogRef.close();
    });
  }

  openFolders(product: Product) {
    const dialogRef = this.dialog.open(FoldersComponent);
    dialogRef.componentInstance.product = product;
  }

  openProps(product: Product) {
    const dialogRef = this.dialog.open(PropertiesComponent);
    dialogRef.componentInstance.product = product;
  }

  uploadFile() {
    const dialogRef = this.dialog.open(UploadComponent);
    dialogRef.componentInstance.doneEvent.subscribe(() => {
      dialogRef.close();
    });
  }

  setNewPrice(product: Product) {
    const dialogRef = this.dialog.open(SetNewPriceComponent);
    dialogRef.componentInstance.product = product;
    dialogRef.componentInstance.saveEvent.subscribe((value) => {
      if (value) dialogRef.close();
    });
  }

  onRightClick(event: MouseEvent) {
    if (!this.matMenuTrigger) return;
    // preventDefault avoids to show the visualization of the right-click menu of the browser
    event.preventDefault();

    // we record the mouse position in our object
    this.menuTopLeftPosition.x = event.clientX + 'px';
    this.menuTopLeftPosition.y = event.clientY + 'px';

    // we open the menu
    // we pass to the menu the information about our object
    this.matMenuTrigger.menuData = { item: this.product };

    // we open the menu
    this.matMenuTrigger.openMenu();
  }
}
